import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { CalendarRange, ChevronLeft, ChevronRight, Download, Clock, User, TrendingUp, Receipt } from "lucide-react";

// Format an ISO date string (yyyy-mm-dd) as a friendly range label.
function weekLabel(startIso, endIso) {
    if (!startIso) return "";
    const s = new Date(startIso + "T00:00:00");
    const e = new Date(endIso + "T00:00:00");
    const sameMonth = s.getMonth() === e.getMonth();
    const opts = { day: "numeric", month: "short" };
    return sameMonth
        ? `${s.toLocaleDateString(undefined, { day: "numeric" })}–${e.toLocaleDateString(undefined, opts)}`
        : `${s.toLocaleDateString(undefined, opts)} – ${e.toLocaleDateString(undefined, opts)}`;
}

// Return the Monday (YYYY-MM-DD) of the week that contains `date`.
function mondayOf(date) {
    const d = new Date(date);
    const day = d.getDay(); // 0=Sun..6=Sat
    const diff = (day === 0 ? -6 : 1 - day);
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 10);
}

function shiftWeek(iso, days) {
    const d = new Date(iso + "T00:00:00");
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

export default function TimesheetsPage() {
    const [week, setWeek] = useState(() => mondayOf(new Date()));
    const [operativeId, setOperativeId] = useState("");
    const [operatives, setOperatives] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    // Load operative list once for the filter dropdown
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/users");
                setOperatives(data);
            } catch (err) {
                // Non-fatal — the dropdown just stays empty. Log so we can
                // diagnose if a manager reports "I can't filter by person".
                console.error("Timesheets: failed to load operatives", err);
            }
        })();
    }, []);

    // Fetch timesheet whenever week or operative filter changes
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const params = { week };
                if (operativeId) params.operative_id = operativeId;
                const { data } = await api.get("/timesheet", { params });
                setData(data);
            } catch (e) {
                toast.error(formatApiError(e));
            } finally { setLoading(false); }
        })();
    }, [week, operativeId]);

    const download = async () => {
        setDownloading(true);
        try {
            const params = { week };
            if (operativeId) params.operative_id = operativeId;
            const res = await api.get("/timesheet.csv", { params, responseType: "blob" });
            const blob = new Blob([res.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `scs_timesheet_${week}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Timesheet downloaded");
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't build CSV");
        } finally { setDownloading(false); }
    };

    const isCurrentWeek = week === mondayOf(new Date());
    const rangeLabel = data ? weekLabel(data.week_start, data.week_end) : "";
    const totals = data?.summary || [];
    const totalHours = data?.total_hours ?? 0;

    // Group rows by operative for nicer display
    const grouped = useMemo(() => {
        const byOp = new Map();
        for (const r of (data?.rows || [])) {
            const key = r.operative_id || "unassigned";
            if (!byOp.has(key)) byOp.set(key, { name: r.operative_name, rows: [] });
            byOp.get(key).rows.push(r);
        }
        return [...byOp.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));
    }, [data]);

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <span className="text-xs uppercase tracking-[0.22em] text-[var(--terracotta)] font-semibold">Payroll</span>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Timesheets</h1>
                    <p className="text-[var(--text-secondary)] mt-1 text-sm">
                        Every clock-in on every job — export a week to CSV for payroll.
                    </p>
                </div>
                <button data-testid="download-csv-btn" onClick={download} disabled={downloading || !data}
                        className="btn-primary inline-flex items-center gap-2 text-sm">
                    <Download size={14} /> {downloading ? "Building…" : "Download CSV"}
                </button>
            </div>

            {/* Week navigator + operative filter */}
            <div className="card-flat p-4 sm:p-5 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1">
                    <button data-testid="week-prev-btn"
                            onClick={() => setWeek(w => shiftWeek(w, -7))}
                            className="btn-ghost !p-2" aria-label="Previous week">
                        <ChevronLeft size={16} />
                    </button>
                    <div className="min-w-[160px] text-center">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">
                            {isCurrentWeek ? "This week" : "Week of"}
                        </div>
                        <div data-testid="week-label" className="font-semibold text-sm mt-0.5">
                            <CalendarRange size={12} className="inline mr-1 -mt-0.5" />
                            {rangeLabel || "…"}
                        </div>
                    </div>
                    <button data-testid="week-next-btn"
                            onClick={() => setWeek(w => shiftWeek(w, 7))}
                            className="btn-ghost !p-2" aria-label="Next week">
                        <ChevronRight size={16} />
                    </button>
                </div>
                <button data-testid="week-today-btn"
                        onClick={() => setWeek(mondayOf(new Date()))}
                        className="btn-ghost text-xs" disabled={isCurrentWeek}>
                    This week
                </button>
                <div className="ml-auto flex items-center gap-2">
                    <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">Operative</label>
                    <select data-testid="operative-filter"
                            value={operativeId}
                            onChange={e => setOperativeId(e.target.value)}
                            className="input-field !py-2 !pr-8 text-sm">
                        <option value="">Everyone</option>
                        {operatives.map(o => (
                            <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Invoicing export: separate from the weekly payroll timesheet.
                Managers pick a client + optional date range → they get a CSV
                of every completed / finalised job for that client with all
                the fields their accountant needs (client contact, site
                address, scheduled + actual hours, team members, notes). */}
            <InvoicingExportCard />

            {/* Totals */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-5" data-testid="totals-grid">
                <SummaryCard icon={<Clock size={18} />} label="Total hours"
                             value={totalHours.toFixed(2)}
                             tint="var(--pine-light)" fg="var(--pine)" />
                <SummaryCard icon={<User size={18} />} label="Operatives with hours"
                             value={String(totals.length)}
                             tint="var(--terracotta-light)" fg="var(--terracotta)" />
                <SummaryCard icon={<TrendingUp size={18} />} label="Sessions"
                             value={String((data?.rows || []).length)}
                             tint="rgba(25,118,210,0.10)" fg="var(--info)" />
            </div>

            {/* Per-operative breakdown */}
            {loading ? (
                <div className="text-sm text-[var(--text-muted)]">Loading timesheet…</div>
            ) : grouped.length === 0 ? (
                <div className="card-flat p-10 text-center text-sm text-[var(--text-muted)]" data-testid="empty-timesheet">
                    <Clock size={28} className="mx-auto text-[var(--text-muted)] opacity-60" />
                    <div className="mt-3 font-semibold text-[var(--text-secondary)]">Nothing clocked in this week</div>
                    <p className="mt-1">When operatives clock in on their jobs, their hours will show up here.</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {grouped.map(([opId, block]) => {
                        const sum = totals.find(t => (t.operative_id || "unassigned") === opId);
                        return (
                            <section key={opId} className="card-flat overflow-hidden" data-testid={`op-block-${opId}`}>
                                <header className="p-4 sm:p-5 flex items-center justify-between border-b border-[var(--border-soft)]">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="h-10 w-10 rounded-full bg-[var(--pine-light)] text-[var(--pine)] flex items-center justify-center font-semibold shrink-0">
                                            {block.name[0]?.toUpperCase() || "?"}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-display text-lg font-semibold truncate">{block.name}</div>
                                            <div className="text-xs text-[var(--text-muted)]">
                                                {block.rows.length} session{block.rows.length === 1 ? "" : "s"}
                                                {sum?.open_sessions ? ` · ${sum.open_sessions} still open` : ""}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">Hours</div>
                                        <div className="font-display text-2xl font-bold" data-testid={`op-hours-${opId}`}>
                                            {(sum?.hours ?? 0).toFixed(2)}
                                        </div>
                                    </div>
                                </header>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-[var(--surface-soft)] text-[var(--text-muted)]">
                                            <tr className="text-left text-[11px] uppercase tracking-[0.14em]">
                                                <th className="px-4 py-2 font-semibold">Date</th>
                                                <th className="px-4 py-2 font-semibold">Client / Job</th>
                                                <th className="px-4 py-2 font-semibold">Clock in</th>
                                                <th className="px-4 py-2 font-semibold">Clock out</th>
                                                <th className="px-4 py-2 font-semibold text-right">Hours</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-[var(--border-soft)]">
                                            {block.rows.map((r, i) => (
                                                <tr key={i} data-testid={`ts-row-${r.job_id}`} className="hover:bg-[var(--surface-soft)]">
                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                        <div className="text-xs uppercase tracking-wide text-[var(--text-muted)]">{r.weekday}</div>
                                                        <div>{r.date}</div>
                                                    </td>
                                                    <td className="px-4 py-3 min-w-[220px]">
                                                        <div className="font-semibold truncate">{r.job_title}</div>
                                                        <div className="text-xs text-[var(--text-muted)] truncate">{r.client_name}</div>
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap font-mono">{r.clock_in}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap font-mono">
                                                        {r.clock_out || (
                                                            <span className="text-orange-700 text-xs font-semibold">still open</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-semibold">
                                                        {r.hours != null ? r.hours.toFixed(2) : "—"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function SummaryCard({ icon, label, value, tint, fg }) {
    return (
        <div className="card-flat p-5 flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
                 style={{ background: tint, color: fg }}>
                {icon}
            </div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">{label}</div>
                <div className="font-display text-2xl font-bold tracking-tight mt-0.5">{value}</div>
            </div>
        </div>
    );
}

function InvoicingExportCard() {
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [statuses, setStatuses] = useState(["completed", "finalised"]);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/clients");
                setClients((data || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")));
            } catch (err) {
                // Non-fatal — client dropdown just stays empty. Log so a
                // failing /clients call doesn't disappear silently.
                console.error("Invoicing export: failed to load clients", err);
            }
        })();
    }, []);

    const toggle = (s) => {
        setStatuses(prev => prev.includes(s)
            ? (prev.length > 1 ? prev.filter(x => x !== s) : prev)
            : [...prev, s]);
    };

    const download = async () => {
        setDownloading(true);
        try {
            const params = {};
            if (clientId) params.client_id = clientId;
            if (start) params.start = start;
            if (end) params.end = end;
            params.include = statuses.join(",");
            const res = await api.get("/invoicing/export.csv", { params, responseType: "blob" });
            const blob = new Blob([res.data], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const clientLabel = clientId
                ? (clients.find(c => c.id === clientId)?.name || "client").replace(/[^a-z0-9]+/gi, "_").toLowerCase()
                : "all_clients";
            const rangeLabel = (start || end) ? `${start || "start"}_to_${end || "today"}` : new Date().toISOString().slice(0, 10);
            a.download = `scs_invoicing_${clientLabel}_${rangeLabel}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Invoicing CSV downloaded");
        } catch (e) {
            toast.error(formatApiError(e) || "Couldn't build CSV");
        } finally { setDownloading(false); }
    };

    return (
        <section data-testid="invoicing-export-card" className="card-flat p-5 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-3">
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
                         style={{ background: "var(--terracotta-light)", color: "var(--terracotta)" }}>
                        <Receipt size={18} />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--terracotta)] font-semibold">
                            Invoicing export
                        </div>
                        <div className="font-display text-lg font-semibold mt-0.5">Completed & finalised jobs → CSV</div>
                        <p className="text-xs text-[var(--text-secondary)] mt-1 max-w-xl">
                            Pick a client (or leave blank for all clients), optionally narrow to a date range, and download a
                            spreadsheet ready to paste into an invoice. Includes job title, site address, total hours, and the team who worked on it.
                        </p>
                    </div>
                </div>
                <button data-testid="invoicing-download-btn"
                        onClick={download}
                        disabled={downloading}
                        className="btn-primary inline-flex items-center gap-2 text-sm shrink-0">
                    <Download size={14} /> {downloading ? "Building…" : "Download CSV"}
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">Client</span>
                    <select data-testid="invoicing-client-select"
                            value={clientId}
                            onChange={e => setClientId(e.target.value)}
                            className="input-field !py-2 !pr-8 text-sm mt-1 w-full">
                        <option value="">All clients</option>
                        {clients.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </label>
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">From</span>
                    <input data-testid="invoicing-start-date"
                           type="date"
                           value={start}
                           onChange={e => setStart(e.target.value)}
                           className="input-field !py-2 text-sm mt-1 w-full" />
                </label>
                <label className="block">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">To</span>
                    <input data-testid="invoicing-end-date"
                           type="date"
                           value={end}
                           onChange={e => setEnd(e.target.value)}
                           className="input-field !py-2 text-sm mt-1 w-full" />
                </label>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">Status</span>
                {[
                    { k: "completed", label: "Completed" },
                    { k: "finalised", label: "Finalised" },
                ].map(s => (
                    <button key={s.k} type="button"
                            data-testid={`invoicing-status-${s.k}`}
                            onClick={() => toggle(s.k)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                                statuses.includes(s.k)
                                    ? "bg-[var(--pine)] text-white border-[var(--pine)]"
                                    : "bg-white text-[var(--text-primary)] border-[var(--border-soft)] hover:border-[var(--pine)]"
                            }`}>
                        {s.label}
                    </button>
                ))}
                {(clientId || start || end) && (
                    <button type="button"
                            data-testid="invoicing-clear-btn"
                            onClick={() => { setClientId(""); setStart(""); setEnd(""); }}
                            className="ml-auto text-xs text-[var(--text-muted)] hover:text-[var(--pine)] hover:underline">
                        Clear filters
                    </button>
                )}
            </div>
        </section>
    );
}

